import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { AuthService } from '../services/auth.service';
import { TenantService } from '../services/tenant.service';
import { ERROR_CONSTANTS, HTTP_STATUS_CODES } from '../constants/error.constants';
import { AUTH_RETRIED, SILENCE_ERROR_TOAST, SKIP_AUTH } from '../constants/http-context.tokens';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {

  constructor(
    private notificationService: NotificationService,
    private injector: Injector
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const skipAuth = req.context.get(SKIP_AUTH) || this.isAnonymousAuthUrl(req.url);
    let authReq = req;

    if (!skipAuth) {
      const token = this.getToken();
      if (token) {
        authReq = this.withAuthHeaders(req, token);
      }
    } else if (!(req.body instanceof FormData) && !req.headers.has('Content-Type')) {
      authReq = req.clone({
        setHeaders: { Accept: 'application/json', 'Content-Type': 'application/json' }
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error.status !== HTTP_STATUS_CODES.UNAUTHORIZED) {
          if (error.status === HTTP_STATUS_CODES.FORBIDDEN) {
            const redirectTenant = error.error?.redirectTenant;
            if (redirectTenant) {
              this.injector.get(TenantService).navigateToTenantChat(redirectTenant);
              return throwError(() => error);
            }
          }
          if (!this.isAnonymousAuthUrl(req.url) && !req.context.get(SILENCE_ERROR_TOAST)) {
            this.handleError(error);
          }
          return throwError(() => error);
        }

        if (skipAuth || req.context.get(AUTH_RETRIED)) {
          return throwError(() => error);
        }

        const authService = this.injector.get(AuthService);
        return authService.refreshAccessToken().pipe(
          switchMap(() => {
            const token = authService.getToken();
            const retried = this.withAuthHeaders(req, token).clone({
              context: req.context.set(AUTH_RETRIED, true)
            });
            return next.handle(retried);
          }),
          catchError((refreshError: unknown) => {
            if (refreshError instanceof HttpErrorResponse
              && refreshError.status !== HTTP_STATUS_CODES.BAD_REQUEST
              && refreshError.status !== HTTP_STATUS_CODES.UNAUTHORIZED) {
              return throwError(() => error);
            }

            return throwError(() => refreshError);
          })
        );
      })
    );
  }

  private withAuthHeaders(req: HttpRequest<any>, token: string | null): HttpRequest<any> {
    const isBlob = req.responseType === 'blob';
    const headers: Record<string, string> = {
      Accept: isBlob ? '*/*' : 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Never force JSON content-type on GET/DELETE or blob downloads — it breaks file endpoints.
    const method = (req.method || 'GET').toUpperCase();
    if (
      !isBlob &&
      method !== 'GET' &&
      method !== 'DELETE' &&
      method !== 'HEAD' &&
      !(req.body instanceof FormData)
    ) {
      headers['Content-Type'] = 'application/json';
    }
    const tenantName = this.injector.get(TenantService).currentTenantName();
    if (tenantName && !/\/Account\/Tenants$/i.test(req.url.split('?')[0])) {
      headers['X-Tenant-Name'] = tenantName;
    }
    return req.clone({ setHeaders: headers });
  }

  private isAnonymousAuthUrl(url: string): boolean {
    const path = url.split('?')[0];
    return /\/Account\/(Authenticate|RefreshToken|LogOut|SignUp)$/i.test(path)
      || /\/forgot-password/i.test(path);
  }

  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private handleError(error: HttpErrorResponse): void {
    // Blob error bodies are handled by the caller (e.g. leave attachment preview).
    if (error.error instanceof Blob) {
      return;
    }

    let errorMessage: string = ERROR_CONSTANTS.UNEXPECTED_ERROR;

    if (error.error instanceof ErrorEvent) {
      errorMessage = `${ERROR_CONSTANTS.CLIENT_ERROR}: ${error.error.message}`;
    } else {
      const serverMessage = error.error?.message;

      switch (error.status) {
        case HTTP_STATUS_CODES.FORBIDDEN:
          errorMessage = serverMessage || ERROR_CONSTANTS.FORBIDDEN;
          break;
        case HTTP_STATUS_CODES.NOT_FOUND:
          errorMessage = serverMessage || ERROR_CONSTANTS.NOT_FOUND;
          break;
        case HTTP_STATUS_CODES.UNPROCESSABLE_ENTITY:
          errorMessage = serverMessage || ERROR_CONSTANTS.VALIDATION_FAILED;
          break;
        case HTTP_STATUS_CODES.INTERNAL_SERVER_ERROR:
          errorMessage = serverMessage || ERROR_CONSTANTS.SERVER_ERROR;
          break;
        default:
          errorMessage = serverMessage || `${ERROR_CONSTANTS.SERVER_ERROR_PREFIX}: ${error.status} - ${error.statusText}`;
      }
    }

    this.notificationService.showFailure(errorMessage);
  }
}
